import { Container, Box, Card, Badge } from '@/ui';
import { Grid } from '@/ui/layout';
import { Heading, Text } from '@/ui/typography';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Gem, Palette, Wand2 } from 'lucide-react';

// Module-level constants to prevent re-renders
const SCROLL_OFFSET: ["start end","end start"] = ["start end","end start"];
const ORB_Y_INPUT: [number, number] = [0, 1];
const ORB_Y_OUTPUT: [number, number] = [50, -50];

const containerVariants = {
 hidden: { opacity: 0 },
 visible: {
 opacity: 1,
 transition: {
 staggerChildren: 0.15,
 delayChildren: 0.1,
 }
 }
} as const;

const itemVariants = {
 hidden: { opacity: 0, y: 30 },
 visible: {
 opacity: 1,
 y: 0,
 transition: {
 duration: 0.8,
 ease: [0.16, 1, 0.3, 1],
 }
 }
} as const;

const colorThemes = {
  emerald: {
    bg: 'hsl(var(--emerald-100))',
    bgDark: 'hsl(var(--emerald-900) / 0.2)',
    text: 'hsl(var(--emerald-600))',
    textDark: 'hsl(var(--emerald-400))',
  },
  amber: {
    bg: 'hsl(var(--amber-100))',
    bgDark: 'hsl(var(--amber-900) / 0.2)',
    text: 'hsl(var(--amber-600))',
    textDark: 'hsl(var(--amber-400))',
  },
  rose: {
    bg: 'hsl(var(--rose-100))',
    bgDark: 'hsl(var(--rose-900) / 0.2)',
    text: 'hsl(var(--rose-600))',
    textDark: 'hsl(var(--rose-400))',
  },
} as const;

const benefits = [
  {
    titleKey: 'ui.creation.benefit1.title',
    descriptionKey: 'ui.creation.benefit1.description',
    icon: Wand2,
    colorTheme: 'emerald' as const,
  },
  {
    titleKey: 'ui.creation.benefit2.title',
    descriptionKey: 'ui.creation.benefit2.description',
    icon: Palette,
    colorTheme: 'amber' as const,
  },
  {
    titleKey: 'ui.creation.benefit3.title',
    descriptionKey: 'ui.creation.benefit3.description',
    icon: Gem,
    colorTheme: 'rose' as const,
  },
] as const;

export const CreationSection = (): React.ReactElement => {
 const { t } = useTranslation();
 const sectionRef = useRef<HTMLElement>(null);

 const { scrollYProgress } = useScroll({
 target: sectionRef,
 offset: SCROLL_OFFSET,
 });
 const orbY = useTransform(scrollYProgress, ORB_Y_INPUT, ORB_Y_OUTPUT);

  return (
    <section
      id="create"
      ref={sectionRef}
      style={{
        paddingTop: 'var(--spacing-20)',
        paddingBottom: 'var(--spacing-20)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated gradient orbs */}
      <motion.div
        style={{
          y: orbY,
          position: 'absolute',
          inset: 0,
          zIndex: -10
        }}
      >
        <Box style={{
          position: 'absolute',
          top: 0,
          left: '25%',
          width: '384px',
          height: '384px',
          background: 'hsl(var(--primary-color) / 0.05)',
          borderRadius: '9999px',
          filter: 'blur(96px)'
        }} />
        <Box style={{
          position: 'absolute',
          bottom: 0,
          right: '25%',
          width: '384px',
          height: '384px',
          background: 'hsl(var(--accent-color) / 0.05)',
          borderRadius: '9999px',
          filter: 'blur(96px)'
        }} />
      </motion.div>

      <Container size="6xl" style={{ position: 'relative', zIndex: 10 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
          style={{ textAlign: 'center', marginBottom: 'var(--spacing-16)' }}
        >
          <motion.div variants={itemVariants}>
            <Badge
              variant="primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginBottom: 'var(--spacing-6)'
              }}
            >
              Benefits
            </Badge>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Heading level={2} size="display-2" weight="normal">
              {t('ui.creation.titleLine1')}
              <br />
              <Text
                as="span"
                weight="normal"
                style={{
                  background: 'linear-gradient(to right, hsl(var(--primary-color)), hsl(var(--accent-color)), hsl(var(--primary-color)))',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  backgroundSize: '200% auto',
                  animation: 'gradient 6s ease infinite',
                }}
              >
                {t('ui.creation.titleLine2')}
              </Text>
            </Heading>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Container size="3xl">
              <Text size="body-lg" color="muted">
                {t('ui.creation.subtitle')}
              </Text>
            </Container>
          </motion.div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={containerVariants}
        >
          <Grid cols={{ base: 1, md: 3 }} gap="lg">
            {benefits.map((item, index) => {
              const Icon = item.icon;
              const theme = colorThemes[item.colorTheme];
              return (
                <motion.div key={index} variants={itemVariants}>
                  <Card
                    variant="outlined"
                    style={{
                      padding: 'var(--spacing-6)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <Box style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: 'var(--radius-lg)',
                      background: theme.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 'var(--spacing-4)'
                    }}>
                      <Icon style={{ width: '24px', height: '24px', color: theme.text }} />
                    </Box>
                    <Heading level={3} size="heading-3">
                      {t(item.titleKey)}
                    </Heading>
                    <Text variant="muted">
                      {t(item.descriptionKey)}
                    </Text>
                  </Card>
                </motion.div>
              );
            })}
          </Grid>
        </motion.div>
      </Container>

      {/* Gradient animation */}
      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </section>
  );
};
